/**
 * ECS-lite: struct-of-arrays component storage with generational handles.
 *
 * Deliberately not a general-purpose ECS. It is a fixed-capacity slab of typed
 * arrays with a component bitmask, because:
 *   - the entity count is bounded and small (hundreds, not millions),
 *   - every hot system wants raw array access with no indirection,
 *   - generational handles make "did this entity die since I stored it?" free.
 *
 * An `Entity` handle packs index and generation into one 32-bit number, so
 * handles can live in typed arrays without boxing.
 */

export type Entity = number;
export const NULL_ENTITY: Entity = -1;

const INDEX_BITS = 20;
const INDEX_MASK = (1 << INDEX_BITS) - 1;
const MAX_ENTITIES = 1 << 14; // 16384 — far above what we'll use

export function entityIndex(e: Entity): number {
  return e & INDEX_MASK;
}
export function entityGen(e: Entity): number {
  return e >>> INDEX_BITS;
}
function makeHandle(index: number, gen: number): Entity {
  return ((gen << INDEX_BITS) | index) >>> 0;
}

// ------------------------------------------------------------- components

export const Comp = {
  TRANSFORM: 1 << 0,
  BODY: 1 << 1,
  SPRITE: 1 << 2,
  HEALTH: 1 << 3,
  PLAYER: 1 << 4,
  ENEMY: 1 << 5,
  PROJECTILE: 1 << 6,
  LIFETIME: 1 << 7,
  STATUS: 1 << 8,
  AI: 1 << 9,
  HITBOX: 1 << 10,
  PICKUP: 1 << 11,
  EMITTER: 1 << 12,
  COMPOSITION: 1 << 13,
  WEAPONS: 1 << 14,
  ATTACHED: 1 << 15,
} as const;
export type CompMask = number;

export const Team = { PLAYER: 0, ENEMY: 1, NEUTRAL: 2 } as const;

/**
 * All component data. One allocation per field at construction; nothing
 * allocates during play.
 */
export class World {
  readonly capacity: number;

  // -- lifecycle
  readonly alive: Uint8Array;
  readonly generation: Uint16Array;
  readonly mask: Int32Array;
  private freeList: number[] = [];
  private highWater = 0;
  private _count = 0;

  // -- transform
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly px: Float32Array; // previous position, for render interpolation
  readonly py: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly facing: Int8Array;
  readonly angle: Float32Array;

  // -- body (collision + material interaction)
  readonly halfW: Float32Array;
  readonly halfH: Float32Array;
  /**
   * How much a crouch has taken off `halfH`, world units, 0 while standing
   * (#98).
   *
   * A *delta* and not the standing height itself, and the difference is the
   * whole reason this column is affordable. `halfH` is written from four
   * places — `resetSlot` below, the archetype at `game.ts`, the enemy def at
   * `ai/enemyAI.ts` and the summon spec at `combat/summons.ts` — and 54 sites
   * across twelve files read it as a constant. A `standHalfH` column would owe
   * every one of those writers a second assignment, and the one that forgot
   * would draw its body at the wrong size for the rest of the run. A delta owes
   * them nothing: it is zero unless a crouch is in progress, `standingHalfH`
   * below adds it back, and a body resized while crouched keeps a correct
   * standing height without anyone being told.
   *
   * `movement.ts` is the only writer. It is also, by being non-zero, the signal
   * that reaches the drawn body — `pixelBodySystem.ts` reads it rather than
   * being told separately, so the folded silhouette and the lowered collider
   * cannot get out of step.
   */
  readonly crouchDrop: Float32Array;
  readonly grounded: Uint8Array;
  readonly onWall: Int8Array; // -1 left, 0 none, 1 right
  readonly submersion: Float32Array; // 0..1
  readonly submergedMat: Uint16Array;
  readonly groundMat: Uint16Array;
  /**
   * Issue #180. Signed downhill grade under the feet, sampled once per
   * `stepBody` call the same way `groundMat` is — 0 while airborne or on
   * flat ground, positive when the surface descends to the right (+x is
   * downhill), negative when it descends to the left. `applyHorizontal`
   * (`movement.ts`) is the one reader; `body.ts`'s `groundSlopeAt` is the
   * one writer.
   */
  readonly groundSlope: Float32Array;
  readonly gravityScale: Float32Array;
  readonly mass: Float32Array;
  readonly bounce: Float32Array;
  readonly bodyFlags: Uint16Array;

  // -- health & combat
  readonly hp: Float32Array;
  readonly maxHp: Float32Array;
  readonly poise: Float32Array;
  readonly maxPoise: Float32Array;
  readonly iframes: Float32Array;
  readonly team: Uint8Array;
  readonly hitstop: Float32Array;
  readonly stagger: Float32Array;

  // -- damage typing derived from composition (see sim/materials)
  readonly coreMat: Uint16Array;
  /** Packed composition: up to 4 (matId, fraction) pairs. */
  readonly compMat: Uint16Array;
  readonly compFrac: Float32Array;

  // -- status effects: parallel arrays, 8 slots per entity
  static readonly STATUS_SLOTS = 8;
  readonly statusKind: Uint8Array;
  readonly statusTime: Float32Array;
  readonly statusStacks: Float32Array;
  /**
   * Seconds of *ambient* exposure accrued in this slot, and the reason it is a
   * fourth array rather than a reading of the third (#120).
   *
   * `applyAmbientStatus` needs a meter: how long has this body been standing in
   * the hazard. It used `statusTime` for that, which is right only while the
   * hazard is the sole author of the status. The moment anything else applies
   * the same kind as a discrete event — a weapon burn, a module's on-hit, an
   * enemy's fire attack — the stored duration is that event's, not the
   * hazard's, and reading it as exposure says the body has already been
   * standing in the fire for as long as the weapon's burn will last. Measured:
   * a module's BURNING (3.0 s) plus one tick of a burning cell read 5 stacks —
   * the cap — where the ladder is authored to take about two seconds.
   *
   * Duration and exposure are two facts about a status and only one of them is
   * the meter, so the meter gets its own storage. It is charged by
   * `applyAmbientStatus`, drained by `tickStatuses` alongside `statusTime`, and
   * cleared everywhere a slot is allocated, evicted or freed. Nothing else may
   * write it: a discrete application deliberately leaves it alone, which is the
   * whole property.
   */
  readonly statusExposure: Float32Array;

  // -- sprite / presentation
  readonly spriteId: Uint16Array;
  readonly spriteFrame: Uint16Array;
  readonly spriteTint: Uint32Array;
  readonly scaleX: Float32Array;
  readonly scaleY: Float32Array;
  readonly flashTime: Float32Array;
  readonly zOrder: Int16Array;

  // -- lifetime / projectile
  readonly lifetime: Float32Array;
  readonly age: Float32Array;
  readonly owner: Int32Array;
  readonly damage: Float32Array;
  readonly damageType: Uint8Array;
  readonly pierce: Int8Array;
  readonly payload: Uint16Array; // module/effect id

  // -- ai
  readonly aiKind: Uint16Array;
  readonly aiState: Uint8Array;
  readonly aiTimer: Float32Array;
  readonly aiTarget: Int32Array;
  readonly aiHome: Float32Array; // x,y interleaved: 2 per entity
  readonly aiBlackboard: Float32Array; // 4 scratch floats per entity

  constructor(capacity = 2048) {
    if (capacity > MAX_ENTITIES) throw new Error('capacity too large');
    this.capacity = capacity;
    const f = (n = 1) => new Float32Array(capacity * n);
    const u8 = () => new Uint8Array(capacity);
    const i8 = () => new Int8Array(capacity);
    const u16 = () => new Uint16Array(capacity);
    const i16 = () => new Int16Array(capacity);
    const i32 = () => new Int32Array(capacity);
    const u32 = () => new Uint32Array(capacity);

    this.alive = u8();
    this.generation = u16();
    this.mask = i32();

    this.x = f();
    this.y = f();
    this.px = f();
    this.py = f();
    this.vx = f();
    this.vy = f();
    this.facing = i8();
    this.angle = f();

    this.halfW = f();
    this.halfH = f();
    this.crouchDrop = f();
    this.grounded = u8();
    this.onWall = i8();
    this.submersion = f();
    this.submergedMat = u16();
    this.groundMat = u16();
    this.groundSlope = f();
    this.gravityScale = f();
    this.mass = f();
    this.bounce = f();
    this.bodyFlags = u16();

    this.hp = f();
    this.maxHp = f();
    this.poise = f();
    this.maxPoise = f();
    this.iframes = f();
    this.team = u8();
    this.hitstop = f();
    this.stagger = f();

    this.coreMat = u16();
    this.compMat = new Uint16Array(capacity * 4);
    this.compFrac = new Float32Array(capacity * 4);

    this.statusKind = new Uint8Array(capacity * World.STATUS_SLOTS);
    this.statusTime = new Float32Array(capacity * World.STATUS_SLOTS);
    this.statusStacks = new Float32Array(capacity * World.STATUS_SLOTS);
    this.statusExposure = new Float32Array(capacity * World.STATUS_SLOTS);

    this.spriteId = u16();
    this.spriteFrame = u16();
    this.spriteTint = u32();
    this.scaleX = f();
    this.scaleY = f();
    this.flashTime = f();
    this.zOrder = i16();

    this.lifetime = f();
    this.age = f();
    this.owner = i32();
    this.damage = f();
    this.damageType = u8();
    this.pierce = i8();
    this.payload = u16();

    this.aiKind = u16();
    this.aiState = u8();
    this.aiTimer = f();
    this.aiTarget = i32();
    this.aiHome = f(2);
    this.aiBlackboard = f(4);
  }

  get count(): number {
    return this._count;
  }

  create(mask: CompMask = Comp.TRANSFORM): Entity {
    let index: number;
    if (this.freeList.length > 0) {
      index = this.freeList.pop()!;
    } else {
      if (this.highWater >= this.capacity) {
        throw new Error(`entity capacity exhausted (${this.capacity})`);
      }
      index = this.highWater++;
    }
    this.alive[index] = 1;
    this.mask[index] = mask;
    this._count++;
    this.resetSlot(index);
    return makeHandle(index, this.generation[index]);
  }

  private resetSlot(i: number): void {
    this.x[i] = this.y[i] = this.px[i] = this.py[i] = 0;
    this.vx[i] = this.vy[i] = 0;
    this.facing[i] = 1;
    this.angle[i] = 0;
    this.halfW[i] = this.halfH[i] = 4;
    this.crouchDrop[i] = 0;
    this.grounded[i] = 0;
    this.onWall[i] = 0;
    this.submersion[i] = 0;
    this.submergedMat[i] = 0;
    this.groundMat[i] = 0;
    this.groundSlope[i] = 0;
    this.gravityScale[i] = 1;
    this.mass[i] = 1;
    this.bounce[i] = 0;
    this.bodyFlags[i] = 0;
    this.hp[i] = this.maxHp[i] = 1;
    this.poise[i] = this.maxPoise[i] = 0;
    this.iframes[i] = 0;
    this.team[i] = Team.NEUTRAL;
    this.hitstop[i] = 0;
    this.stagger[i] = 0;
    this.coreMat[i] = 0;
    this.spriteId[i] = 0;
    this.spriteFrame[i] = 0;
    this.spriteTint[i] = 0xffffffff;
    this.scaleX[i] = this.scaleY[i] = 1;
    this.flashTime[i] = 0;
    this.zOrder[i] = 0;
    this.lifetime[i] = 0;
    this.age[i] = 0;
    this.owner[i] = NULL_ENTITY;
    this.damage[i] = 0;
    this.damageType[i] = 0;
    this.pierce[i] = 0;
    this.payload[i] = 0;
    this.aiKind[i] = 0;
    this.aiState[i] = 0;
    this.aiTimer[i] = 0;
    this.aiTarget[i] = NULL_ENTITY;
    const s = i * World.STATUS_SLOTS;
    for (let k = 0; k < World.STATUS_SLOTS; k++) {
      this.statusKind[s + k] = 0;
      this.statusTime[s + k] = 0;
      this.statusStacks[s + k] = 0;
      this.statusExposure[s + k] = 0;
    }
    const c = i * 4;
    for (let k = 0; k < 4; k++) {
      this.compMat[c + k] = 0;
      this.compFrac[c + k] = 0;
      this.aiBlackboard[c + k] = 0;
    }
    this.aiHome[i * 2] = this.aiHome[i * 2 + 1] = 0;
  }

  destroy(e: Entity): void {
    const i = entityIndex(e);
    if (!this.valid(e)) return;
    this.alive[i] = 0;
    this.mask[i] = 0;
    this.generation[i] = (this.generation[i] + 1) & 0xfff;
    this.freeList.push(i);
    this._count--;
  }

  /** True if the handle still refers to a live entity (generation matches). */
  valid(e: Entity): boolean {
    if (e === NULL_ENTITY) return false;
    const i = entityIndex(e);
    return i < this.capacity && this.alive[i] === 1 && this.generation[i] === entityGen(e);
  }

  has(e: Entity, comp: CompMask): boolean {
    const i = entityIndex(e);
    return this.alive[i] === 1 && (this.mask[i] & comp) === comp;
  }

  add(e: Entity, comp: CompMask): void {
    this.mask[entityIndex(e)] |= comp;
  }

  remove(e: Entity, comp: CompMask): void {
    this.mask[entityIndex(e)] &= ~comp;
  }

  handleAt(index: number): Entity {
    return makeHandle(index, this.generation[index]);
  }

  /**
   * Iterate live indices matching a mask. Yields raw indices — callers index
   * the component arrays directly. Uses a callback rather than a generator
   * because generators allocate per iteration.
   */
  each(mask: CompMask, fn: (i: number) => void): void {
    const { alive, mask: m } = this;
    for (let i = 0; i < this.highWater; i++) {
      if (alive[i] === 1 && (m[i] & mask) === mask) fn(i);
    }
  }

  /** Collect matching indices into a reusable buffer. Returns the count. */
  query(mask: CompMask, out: Int32Array): number {
    const { alive, mask: m } = this;
    let n = 0;
    for (let i = 0; i < this.highWater && n < out.length; i++) {
      if (alive[i] === 1 && (m[i] & mask) === mask) out[n++] = i;
    }
    return n;
  }

  get liveCount(): number {
    return this._count;
  }
  get slotsUsed(): number {
    return this.highWater;
  }

  clear(): void {
    this.alive.fill(0);
    this.mask.fill(0);
    this.freeList.length = 0;
    this.highWater = 0;
    this._count = 0;
  }
}

/**
 * The half-height this body has when it is not crouching (#98).
 *
 * The one thing a crouch changes that most readers of `halfH` must **not**
 * see. `halfH` is the collision box and it is genuinely lower while crouched;
 * this is the size the *drawn* body is scaled against, and it has to stay
 * constant or the figure shrinks instead of squatting — `bodyQuad`'s scale is
 * `halfH * 2 / plan.height` and it multiplies bone thicknesses as well as bone
 * lengths, so a crouch expressed through it deletes 70% of a body's pixels
 * (#98). Two call sites in `pixelBodySystem.ts` and two in `game.ts` want this;
 * every other read of `halfH` in the codebase wants the collider and is
 * correct as it stands.
 *
 * A function rather than a column so there is one subtraction and no second
 * thing to keep true. See `World.crouchDrop`.
 */
export function standingHalfH(w: World, i: number): number {
  return w.halfH[i] + w.crouchDrop[i];
}

// -------------------------------------------------------------- body flags

export const BodyFlag = {
  NO_GRAVITY: 1 << 0,
  IGNORE_TERRAIN: 1 << 1,
  PHASED: 1 << 2,
  FLOATS: 1 << 3,
  DISPLACES_CELLS: 1 << 4,
  BURROWS: 1 << 5,
  STICKY: 1 << 6,
  /** Skips the step-up assist — for projectiles and debris. */
  NO_STEP_UP: 1 << 7,
  /**
   * Ignore ticket for one-way floors: while set, semi-solid cells are air.
   *
   * It has to live on the entity rather than on the cell. The body integrates
   * at 120 Hz with a swept sweep, so "drop through" expressed as a change to
   * the world would re-collide on the very next substep and the player would
   * stutter on the surface they are trying to leave (docs/10 §2.3). The
   * movement layer raises it for a short window on down+jump and clears it
   * again; body.ts only honours it.
   */
  DROP_THROUGH: 1 << 8,
} as const;

// ------------------------------------------------------------ damage types

export const DamageType = {
  PHYSICAL: 0,
  FIRE: 1,
  FROST: 2,
  SHOCK: 3,
  ACID: 4,
  TOXIC: 5,
  VOID: 6,
  RADIANT: 7,
  BLOOD: 8,
  TEMPORAL: 9,
  FUNGAL: 10,
  NANITE: 11,
} as const;
export type DamageTypeId = (typeof DamageType)[keyof typeof DamageType];

export const DAMAGE_TYPE_NAMES = [
  'physical', 'fire', 'frost', 'shock', 'acid', 'toxic',
  'void', 'radiant', 'blood', 'temporal', 'fungal', 'nanite',
];
